import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Stack, TextField, Typography } from '@munifocus/ui';
import { AuthShell } from '../components/auth/AuthShell';
import { BrandMark } from '../components/BrandMark';
import { sha256OfFile, useSignedDocumentVerification } from '../lib/signoff-api';
import { formatDateTime } from '../lib/relative-time';

/**
 * Public verification of a signed document. No account: whoever holds the
 * paper (or the PDF) types the reference printed on the signature block, and
 * may pick the PDF to check its fingerprint. The file never leaves the browser
 * — only its SHA-256 is sent.
 */
export function VerifyDocument() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const prefill = (params.get('ref') ?? '').trim().slice(0, 24);
  const [reference, setReference] = useState(prefill);
  const [sha256, setSha256] = useState<string | undefined>();
  const [fileName, setFileName] = useState<string | null>(null);
  // The check that was asked for. Seeded from the link printed on the
  // document, so arriving from it verifies at once. A query rather than a
  // mutation fired from a mount effect: under StrictMode the effect's
  // mutation observer is unsubscribed by the dev remount and never sees the
  // answer, leaving the page "checking" forever.
  const [check, setCheck] = useState<{ reference: string; sha256?: string } | null>(
    prefill ? { reference: prefill } : null,
  );
  const verify = useSignedDocumentVerification(check);

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileName(file.name);
    setSha256(await sha256OfFile(file));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!reference.trim() || verify.isFetching) return;
    setCheck({ reference: reference.trim(), sha256 });
  };

  const result = verify.data;

  return (
    <AuthShell top={<BrandMark />}>
      <Box component="form" onSubmit={submit} noValidate>
        <Stack spacing={2}>
          <Typography variant="h4" component="h1">
            {t('gov.signoff.verify.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('gov.signoff.verify.lead')}
          </Typography>
          <TextField
            label={t('gov.signoff.verify.reference')}
            value={reference}
            onChange={(e) => setReference(e.target.value.toUpperCase())}
            slotProps={{ htmlInput: { maxLength: 24 } }}
            fullWidth
          />
          <Box>
            <Button variant="outlined" component="label">
              {t('gov.signoff.verify.pickFile')}
              <input type="file" accept="application/pdf" hidden onChange={onPick} />
            </Button>
            {fileName && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {t('gov.signoff.verify.fileChosen', { name: fileName })}
              </Typography>
            )}
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={!reference.trim() || verify.isFetching}
          >
            {t('gov.signoff.verify.submit')}
          </Button>

          {verify.isError && <Alert severity="error">{t('gov.signoff.verify.failed')}</Alert>}
          {result && !result.valid && (
            <Alert severity="error">{t('gov.signoff.verify.notFound')}</Alert>
          )}
          {result?.valid && (
            <Alert severity={result.hashMatches === false ? 'warning' : 'success'}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {t('gov.signoff.verify.valid')}
              </Typography>
              <Typography variant="body2">
                {t('gov.signoff.verify.details', {
                  document: t(`gov.forms.kind.${result.formKind}`, {
                    defaultValue: result.formKind,
                  }),
                  name: result.signerName,
                  municipality: result.localGovernmentName,
                  at: formatDateTime(result.signedAt ?? '', i18n.language),
                })}
              </Typography>
              {result.signerTitle && <Typography variant="body2">{result.signerTitle}</Typography>}
              {result.hashMatches === true && (
                <Typography variant="body2">{t('gov.signoff.verify.hashMatches')}</Typography>
              )}
              {result.hashMatches === false && (
                <Typography variant="body2">{t('gov.signoff.verify.hashMismatch')}</Typography>
              )}
              <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-all' }}>
                SHA-256: {result.sha256}
              </Typography>
            </Alert>
          )}
        </Stack>
      </Box>
    </AuthShell>
  );
}
